<template>
  <div class="manageProgramsDatatable">
    <DatatableReportV3
      ref="datatableRef"
      class="mb-2"
      scrollable
      scrollable-height="calc(100vh - 430px)"
      subpage
      bulk-select
      no-download
      :filters="filters"
      :source="source"
      :config="config"
      :bulk-select-ids="selectedPrograms"
      @row-select="(row, checked) => handleRowSelect(row, checked)"
      @update:bulk-select-ids="handleSelectionUpdate"
      @draw-complete="updateStatusFilter"
    >
      <template #column-description="{ columnValue }">
        <div class="manageProgramsDatatable__description">
          {{ columnValue }}
        </div>
      </template>
      <template #column-preferred_min_amount="row">
        <div class="d-flex">
          <div class="ml-auto d-flex align-items-center">
            <span>{{ row.columnValue || "--" }}</span>
            <SeraButton
              data-cy="editMinFinanceButton"
              icon="fa-regular fa-edit"
              class="ml-2 editIcon"
              @click="handleShowAdjustMinFinanceAmountModal(row)"
            />
          </div>
        </div>
      </template>
    </DatatableReportV3>
    <FinanceProgramStatusUpdater
      class="financePogramStatusUpdater"
      :programs="selectedPrograms"
      :status-filter="statusFilter"
      @deactivate="handleDeactivatePrograms"
      @activate="handleActivatePrograms"
    />
    <AdjustMinimumFinanceAmountModal
      v-if="showAdjustMinFinanceAmountModal"
      :id="selectedProgramToEdit?.id"
      @hide-modal="showAdjustMinFinanceAmountModal = false"
      @save-success="handleSaveSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus";

import SeraButton from "@/components/Button.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import { LoanProgram, LoanProgramDTResponse } from "../types";
import AdjustMinimumFinanceAmountModal from "./AdjustMinimumFinanceAmountModal.vue";
import FinanceProgramStatusUpdater from "./FinanceProgramStatusUpdater.vue";

const props = defineProps({
  filters: {
    type: Array,
    default: () => []
  },
  lender: {
    type: String,
    default: ""
  },
  value: {
    type: Array as () => LoanProgramDTResponse[],
    default: () => []
  }
});

const store = useStore();
const selectedPrograms = ref<LoanProgramDTResponse[]>([]);
const showAdjustMinFinanceAmountModal = ref(false);
const datatableRef = ref<any>(null);
const selectedProgramToEdit = ref<LoanProgram | null>(null);
const statusFilter = ref(null);

const source = `/loan_programs.json?lender=${props.lender}`;
const config = {
  order: [1, "asc"],
  columns: [
    {
      data: "id",
      visible: false,
      searchable: false
    },
    {
      data: "status",
      className: "column-status",
      title: "Status",
      orderable: false,
      searchable: false
    },
    {
      data: "external_id",
      title: "Program #",
      orderable: false
    },
    {
      data: "description",
      class: "column-description",
      title: "Program Description",
      orderable: false
    },
    {
      data: "dealer_fee_percentage",
      title: "Dealer Fee",
      searchable: false,
      className: "text-right"
    },
    {
      data: "min_amount",
      title: "Min Financed Amt",
      searchable: false,
      className: "text-right"
    },
    {
      data: "preferred_min_amount",
      title: "Prefer Min Amt",
      searchable: false,
      className: "text-right"
    },
    {
      data: "max_amount",
      title: "Max Financed Amt",
      searchable: false,
      className: "text-right"
    },
    {
      data: "max_interest_rate",
      title: "Max Int. Rate",
      searchable: false,
      className: "text-right"
    }
  ]
};

const selectedProgramIds = computed(() => {
  return selectedPrograms.value.map((row) => parseInt(row.id));
});

onMounted(() => window.addEventListener("beforeunload", displayWarning));
onUnmounted(() => window.removeEventListener("beforeunload", displayWarning));

function displayWarning(e) {
  if (selectedProgramIds.value.length) {
    e.preventDefault();
    return (e.returnValue = "Changes you made may not be saved.");
  }
}

function handleRowSelect(row, checked) {
  const rowIndex = selectedPrograms.value.findIndex(
    (selectedProgramRow) => selectedProgramRow.id === row.id
  );
  if (checked) selectedPrograms.value.push(row);
  else if (rowIndex > -1) {
    selectedPrograms.value.splice(rowIndex, 1);
  }
}

function handleSelectionUpdate(ids) {
  if (ids.length === 0) {
    selectedPrograms.value = [];
  }
}

function handleShowAdjustMinFinanceAmountModal(program: LoanProgram) {
  showAdjustMinFinanceAmountModal.value = true;
  selectedProgramToEdit.value = program;
}

function handleSaveSuccess() {
  EventBus.$emit("refresh-datatable");
}

async function handleActivatePrograms() {
  await store.dispatch("activateLoanPrograms", {
    lender: props.lender,
    programIds: selectedProgramIds.value
  });
  selectedPrograms.value = [];
  window.notify.success(`Programs have been activated.`);
  EventBus.$emit("refresh-datatable");
}

async function handleDeactivatePrograms() {
  await store.dispatch("deactivateLoanPrograms", {
    lender: props.lender,
    programIds: selectedProgramIds.value
  });
  selectedPrograms.value = [];
  EventBus.$emit("refresh-datatable");
  window.notify.success(`Programs have been deactivated.`);
}

function updateStatusFilter() {
  const urlParams = new URLSearchParams(window.location.search);
  statusFilter.value = urlParams.get("status");
}
</script>

<style lang="scss">
.manageProgramsDatatable {
  .editIcon {
    font-size: 1.2em;
  }
  td.column-description {
    white-space: normal;
  }
  .column-status {
    min-width: 100px;
  }
  .column-description {
    min-width: 400px;
  }
}
</style>
